/**
 * The JS bridge to the native shell (technical-design §11.1, §11.8; dwf-032 owns the
 * Kotlin side). The device token lives in `EncryptedSharedPreferences` and is read
 * over this bridge — never `localStorage`. Everything here is optional: the same
 * bundle runs in a browser tab for QA with no shell at all, and nothing about
 * playback depends on it (decision-log DF9: no Android APIs in the bundle).
 */

export interface ShellBridge {
  getDeviceToken?(): string | null;
  getPairingCode?(): string | null;
  /**
   * D251: an SVG QR of the console's Connect a TV link for the code above, which the pairing card
   * shows beside it so staff can scan instead of typing. `null` when the API was not given the
   * console's address, and absent on a shell older than bridge version 7.
   */
  getPairingQr?(): string | null;
  /**
   * D254: forget the device token and the screen it was for, so the shell asks for a new
   * pairing code and the TV can be connected again. Called when the API refuses the token
   * (retired or revoked). Absent on a shell older than bridge version 8.
   */
  clearDeviceCredentials?(): void;
  getFreeBytes?(): number | null;
  getAppVersion?(): string | null;
  /**
   * The Wi-Fi SSID the stick is joined to right now, for the relocation check in
   * decision-log D174. `null` is ordinary, not exceptional: a stick on Ethernet has no SSID,
   * and Android will not hand one to an app without the location permission and location
   * services switched on. The server reads a missing value as "cannot compare".
   */
  getWifiSsid?(): string | null;
  /**
   * Proof of life for the shell's watchdog, which restarts the WebView when it has not been
   * called for 90 seconds (`WatchdogPolicy.playerHeartbeatStaleMs`). It is not the heartbeat the
   * API receives — that one is `POST /device/heartbeat` on the manifest's
   * `heartbeat_interval_seconds` — and the two are easy to confuse, which is part of how this
   * went missing: the shell exposed `reportAlive()`, the watchdog waited on it, the injected
   * `window.dwShell` never listed it, and the player never called it. The watchdog then restarted
   * a perfectly healthy player every 150 seconds, and the API heartbeat's timer never survived
   * long enough to fire, so every playing screen read as dark on the fleet dashboard.
   *
   * Optional like everything else here: in a browser tab there is no shell and nothing restarts.
   */
  reportAlive?(): void;
  /**
   * dwf-063 / decision-log D211: whether the shell's activity holds the foreground right
   * now (`ShellState.activityVisible`, the same value the watchdog reads to decide whether
   * a bring-to-front worked). `null` whenever there is no shell to ask.
   *
   * The player does not act on this — the loop is stopped by `visibilitychange`, not by a
   * poll — it only puts it on the heartbeat, so the fleet dashboard can tell a screen that
   * is playing from one showing Amazon's launcher.
   */
  getForeground?(): boolean | null;
  /**
   * dwf-064 / decision-log D213, the census the fleet keeps current for itself:
   * `Build.MODEL`, the OS build string, `Build.VERSION.SDK_INT`, and whether the
   * `SYSTEM_ALERT_WINDOW` app-op is actually held. The last one is a hardware verdict —
   * without that app-op the watchdog's activity start is refused (D179) and the screen
   * recovers from nothing — and it is known only to the device.
   */
  getHardwareModel?(): string | null;
  getOsVersion?(): string | null;
  getSdkInt?(): number | null;
  getOverlayOpGranted?(): boolean | null;
  /**
   * dwf-064: `wifi` | `ethernet` | `unknown`. `wifi_ssid` alone cannot distinguish a wired
   * screen from a shell that failed to read an SSID, so D174's relocation check silently
   * stops working on the built-in panels D213 says the fleet actually is.
   */
  getNetworkType?(): string | null;
  /**
   * dwf-064 / decision-log D213: whether the television panel itself is on
   * (`PowerManager.isInteractive`). The fleet is Fire TV televisions, so a panel switched into
   * standby may keep heartbeating; without this it reads like the launcher blip after a Home
   * press (D211), though only someone at the venue can fix it. `null` is "cannot tell", never
   * off.
   */
  getScreenOn?(): boolean | null;
  /**
   * dwf-064 §2: SHA-256 of the shell APK's signing certificate, lowercase hex, no colons —
   * which signing identity (debug or release key) this device runs.
   */
  getSigningSha256?(): string | null;
  /** Native mirror of the event queue (technical-design §11.6). */
  mirrorEvents?(json: string): void;
  /**
   * Subscribe to `dw/fleet/blocklist` MQTT payloads forwarded by the shell. The
   * payload is `{"blocklist": ["crv_...", ...]}` per the manifests service.
   */
  onFleetBlocklist?(handler: (payload: unknown) => void): void;
  /**
   * Subscribe to manifest nudges on this screen's `dw/screen/{screen_id}/manifest`
   * topic, forwarded by the shell. The payload is `{"version": N}` (D37) and the
   * handler's job is to fetch early; missing the channel entirely costs one poll
   * interval and nothing else (architecture-v2 §4.3).
   */
  onManifestNudge?(handler: (payload: unknown) => void): void;
}

interface ShellWindow {
  dwShell?: ShellBridge;
}

export function detectShellBridge(host: unknown): ShellBridge {
  if (typeof host !== 'object' || host === null) return {};
  return (host as ShellWindow).dwShell ?? {};
}

/**
 * Token source. A browser tab for QA can pass `?device_token=` so the player is
 * testable without the shell; that is a QA affordance and the shell is the only
 * source on a real screen.
 */
export function createTokenReader(options: {
  readonly bridge: ShellBridge;
  readonly search?: string;
}): () => string | null {
  const fromQuery = readQueryParam(options.search ?? '', 'device_token');
  return () => options.bridge.getDeviceToken?.() ?? fromQuery;
}

export function createPairingCodeReader(options: {
  readonly bridge: ShellBridge;
  readonly search?: string;
}): () => string | null {
  const fromQuery = readQueryParam(options.search ?? '', 'pairing_code');
  return () => options.bridge.getPairingCode?.() ?? fromQuery;
}

/** D251: the QR beside the code. Only the shell has one; there is no query-string stand-in. */
export function createPairingQrReader(options: {
  readonly bridge: ShellBridge;
}): () => string | null {
  return () => options.bridge.getPairingQr?.() ?? null;
}

function readQueryParam(search: string, name: string): string | null {
  if (search.length === 0) return null;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const value = params.get(name);
  return value === null || value.length === 0 ? null : value;
}
